<template>
  <BaseWidget
    :title="widgetTitle"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    :show-info-icon="true"
    :info-tooltip="infoTooltip"
    :info-tooltip-link="infoTooltipLink"
    :overflow-menu-items="taxMenuItems"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
    @overflow-select="handleOverflowSelect"
  >
    <template #header-chip>
      <WidgetInfoIcon
        :tooltip="infoTooltip"
        :link="infoTooltipLink"
      >
        <template #trigger>
          <span class="updated-chip">Updated</span>
        </template>
      </WidgetInfoIcon>
    </template>
    <div
      v-if="loading"
      class="widget-content-wrapper"
    >
      <SkeletonLoader
        type="widget"
        :show-table="showAccountsTable"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue
        :value="revenueCollectedFormatted"
        :subtitle="subtitleText"
        :has-data-table="showAccountsTable && accounts.length > 0"
      >
        <template #comparison>
          <ComparisonChip
            :change="changePercentage"
            :previous-value="previousRevenueCollected"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Revenue collected"
          />
        </template>
        <template #subtitle-suffix>
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>

      <div
        v-if="showAccountsTable && accounts.length > 0"
        class="accounts-list"
      >
        <div
          v-for="account in displayAccounts"
          :key="account.id"
          class="account-item"
          :class="{ clickable: account.account_id }"
          @click="account.account_id && handleAccountClickGuarded(account)"
        >
          <div class="account-row">
            <div class="user-image">
              <img
                v-if="account.avatar"
                :src="account.avatar"
                :alt="account.fullName"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(account.fullName) }}
              </div>
            </div>
            <div class="account-details">
              <div class="account-name">
                {{ account.fullName }}
              </div>
              <div class="account-meta">
                {{ account.revenueFormatted }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="showAccountsTable && accounts.length === 0"
        class="no-data"
      >
        No payments found
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import WidgetInfoIcon from './WidgetInfoIcon.vue'
import { useRevenueCollectedData } from '../composables/useWidgetData.js'
import { DATE_RANGE_LABELS, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { useDemoMode } from '../composables/useDemoMode.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Shared by the info (i) icon and the "Updated" chip so both surface the same tooltip.
const infoTooltip = 'Total payments received within the selected date range, based on when each payment was made (the payment date, not the invoice due date). Excludes refunds.'
const infoTooltipLink = { url: 'https://support.optixapp.com/en/articles/4753023-quick-guide-to-invoices', text: 'Learn about Invoices' }

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_30_days')
const excludeTax = ref(props.props?.excludeTax || false)

const widgetTitle = 'Revenue collected'

const taxMenuItems = computed(() => [
  { label: 'Exclude tax', value: 'exclude-tax', active: excludeTax.value },
  { label: 'Include tax', value: 'include-tax', active: !excludeTax.value }
])

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days'
})

// The excl-tax state lives in the subtitle (not the title) so a longer title can't wrap
// and push the date label / View all out of the widget. See SDWEB-18441.
const subtitleText = computed(() => {
  return excludeTax.value ? `${dateRangeLabel.value} · Excl. tax` : dateRangeLabel.value
})

// Show the per-account breakdown table in the detailed (non-focused) view.
const showAccountsTable = computed(() => !props.focused && props.rowSpan >= 2)

// Get previous period timestamps for tooltip
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value)
})

const { data, loading, error, cleanup, refresh } = useRevenueCollectedData({
  variables: computed(() => ({ dateRange: dateRange.value, excludeTax: excludeTax.value })),
  priority: 1
})

// Fetch previous period data for comparison
const {
  data: previousData,
  loading: previousLoading,
  cleanup: previousCleanup
} = useRevenueCollectedData({
  dataKey: 'revenueCollected_previous',
  variables: computed(() => {
    const prev = getPreviousPeriodTimestamps(dateRange.value)
    return {
      previousPeriod: true,
      fromDate: prev.from_timestamp,
      toDate: prev.to_timestamp,
      excludeTax: excludeTax.value
    }
  }),
  priority: 1
})

const revenueCollectedAmount = computed(() => {
  return data.value?.revenueCollectedTotal || 0
})

const formatCurrency = (amount) => {
  const currencySymbol = data.value?.currency_symbol || '$'
  const locale = (data.value?.locale || 'en-US').replace(/_/g, '-')
  const currency = data.value?.currency || 'USD'
  if (amount === 0) return `${currencySymbol}0`
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (e) {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const revenueCollectedFormatted = computed(() => {
  return formatCurrency(revenueCollectedAmount.value)
})

const previousRevenueCollected = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  const amount = previousData.value?.revenueCollectedTotal || 0
  if (amount === 0) return null
  return formatCurrency(amount)
})

const changePercentage = computed(() => {
  if (loading.value || previousLoading.value) return null
  if (previousData.value === null || previousData.value === undefined) return null

  const current = revenueCollectedAmount.value
  const previous = previousData.value?.revenueCollectedTotal || 0

  if (previous === 0 && current === 0) return null
  if (previous === 0 && current > 0) return 100

  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10
})

const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Per-account breakdown for the detailed view
const accounts = computed(() => {
  const raw = data.value?.accounts
  if (!Array.isArray(raw)) return []
  return raw.map((a) => ({
    id: a.account_id || a.name,
    account_id: a.account_id,
    fullName: a.name,
    avatar: a.avatar || null,
    revenue: a.revenue,
    revenueFormatted: formatCurrency(a.revenue)
  }))
})

const displayAccounts = computed(() => accounts.value.slice(0, 3))

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) { /* ignore */ }
  }
  return { client_id: clientId, canvasUUID }
}

const demoMode = useDemoMode()

const openViewAllPanel = () => {
  pushAdminSidePanel('view-all-revenue-collected', {
    dateRange: dateRange.value,
    excludeTax: excludeTax.value ? '1' : '',
    trial: demoMode.value === true ? '1' : ''
  })
}

const { guard } = useTrialModeGuard()
const handleAccountClickGuarded = guard((account) => {
  const accountId = account.account_id
  if (!accountId || (typeof accountId === 'string' && !/^\d+$/.test(accountId))) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(accountId),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange, excludeTax: excludeTax.value })
  }
}

const handleOverflowSelect = (value) => {
  const newExcludeTax = value === 'exclude-tax'
  if (newExcludeTax !== excludeTax.value) {
    excludeTax.value = newExcludeTax
    emit('update:props', { dateRange: dateRange.value, excludeTax: newExcludeTax })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

watch(() => props.props?.excludeTax, (newVal) => {
  if (newVal !== undefined && newVal !== excludeTax.value) {
    excludeTax.value = newVal
  }
})

// Watch for date range or excludeTax changes and refresh data
watch(dateRange, () => {
  refresh(true, { showLoading: true })
})

watch(excludeTax, () => {
  refresh(true, { showLoading: true })
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  align-self: flex-end;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* "Updated" chip in the widget header */
.updated-chip {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--color-bg-green, #DBEDDB);
  color: #1C3829;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: help;
}

/* Accounts breakdown - copies the Top bookers list layout (no column headers) */
.accounts-list {
  margin-top: 0;
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  overflow: hidden;
}

.account-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.account-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.account-item:last-child {
  border-bottom: none;
}

.account-item.clickable {
  cursor: pointer;
}

.account-item.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.account-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  width: 100%;
}

.account-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.account-name {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}
</style>
